import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Container, Eyebrow } from "@/components/common/Section";
import Reveal from "@/components/common/Reveal";
import { FieldText, FieldTextarea, isEmail } from "@/components/common/FormField";
import { submitDemoRequest } from "@/lib/apiClient";
import { INTEREST_OPTIONS, slugToInterest } from "@/data/solutions";

const initial = {
  name: "",
  email: "",
  organization: "",
  role: "",
  org_type: "",
  team_size: "",
  phone: "",
  interest: "",
  message: "",
};

const orgTypes = ["Government / Municipality", "State / Regulatory Agency", "Enterprise", "Other"];
const teamSizes = ["1–10", "11–50", "51–200", "200+"];

const benefits = [
  "A tailored walkthrough of Licara for your workflows",
  "Guidance on migration from spreadsheets or legacy tools",
  "Security, integration, and deployment options",
  "A clear rollout plan and timeline",
];

export default function DemoRequest() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() => ({
    ...initial,
    interest: slugToInterest(searchParams.get("interest")) || "Licara",
  }));
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!isEmail(form.email)) e.email = "Enter a valid email address.";
    if (!form.organization.trim()) e.organization = "Please enter your organization.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await submitDemoRequest({
        name: form.name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim(),
        role: form.role.trim() || null,
        org_type: form.org_type || null,
        team_size: form.team_size || null,
        phone: form.phone.trim() || null,
        interest: form.interest || null,
        message: form.message.trim() || null,
      });
      toast.success(res.message || "Demo request received!");
      setDone(true);
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="demo-page">
      <section className="relative overflow-hidden">
        <div className="hero-glow pointer-events-none absolute inset-0" />
        <div className="bg-sentira-grid bg-radial-fade pointer-events-none absolute inset-0 opacity-60" />
        <Container className="relative py-20 md:py-24">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <Reveal>
                <Eyebrow>Request a demo</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-5 text-4xl font-semibold leading-[1.08] tracking-[-0.02em] sm:text-5xl">
                  See Licara in action
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
                  Book a personalized walkthrough. We'll show how Licara unifies
                  permits, inspections, citations, and cases for your team.
                </p>
              </Reveal>
              <div className="mt-10 space-y-4">
                {benefits.map((b, i) => (
                  <Reveal key={b} delay={0.12 + i * 0.06}>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                      <p className="text-sm text-muted-foreground">{b}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <Reveal delay={0.1} y={20}>
              <div className="rounded-[var(--radius-card)] border border-border bg-card p-6 shadow-[var(--shadow-card)] md:p-8">
                {done ? (
                  <div className="flex flex-col items-center py-10 text-center" data-testid="demo-success-state">
                    <CheckCircle2 className="h-12 w-12 text-primary" />
                    <h3 className="mt-4 text-xl font-semibold">Request received</h3>
                    <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                      Thanks! Our team will reach out to schedule your Licara
                      demo shortly.
                    </p>
                    <Button
                      variant="secondary"
                      className="mt-6"
                      onClick={() => setDone(false)}
                      data-testid="demo-submit-another-button"
                    >
                      Submit another request
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="space-y-5" data-testid="demo-form">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <FieldText id="d-name" label="Full name" required value={form.name} onChange={set("name")} error={errors.name} testId="demo-request-form-name-input" placeholder="Jane Doe" />
                      <FieldText id="d-email" label="Work email" required type="email" value={form.email} onChange={set("email")} error={errors.email} testId="demo-request-form-email-input" placeholder="jane@agency.gov" />
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <FieldText id="d-org" label="Organization" required value={form.organization} onChange={set("organization")} error={errors.organization} testId="demo-request-form-organization-input" placeholder="City of Springfield" />
                      <FieldText id="d-role" label="Your role" value={form.role} onChange={set("role")} testId="demo-request-form-role-input" placeholder="Director of Operations" />
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-sm">Organization type</Label>
                        <Select value={form.org_type} onValueChange={set("org_type")}>
                          <SelectTrigger data-testid="demo-request-form-orgtype-select" className="bg-[hsl(220_30%_8%)] border-border">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {orgTypes.map((o) => (
                              <SelectItem key={o} value={o}>{o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Team size</Label>
                        <Select value={form.team_size} onValueChange={set("team_size")}>
                          <SelectTrigger data-testid="demo-request-form-teamsize-select" className="bg-[hsl(220_30%_8%)] border-border">
                            <SelectValue placeholder="Select size" />
                          </SelectTrigger>
                          <SelectContent>
                            {teamSizes.map((o) => (
                              <SelectItem key={o} value={o}>{o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Interested in</Label>
                      <Select value={form.interest} onValueChange={set("interest")}>
                        <SelectTrigger data-testid="demo-request-form-interest-select" className="bg-[hsl(220_30%_8%)] border-border">
                          <SelectValue placeholder="Select a solution" />
                        </SelectTrigger>
                        <SelectContent>
                          {INTEREST_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <FieldText id="d-phone" label="Phone (optional)" value={form.phone} onChange={set("phone")} testId="demo-request-form-phone-input" placeholder="(555) 123-4567" />
                    <FieldTextarea id="d-msg" label="What would you like to see?" rows={4} value={form.message} onChange={set("message")} testId="demo-request-form-message-input" placeholder="Tell us about your current workflows and goals..." />
                    <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-[hsl(173_80%_34%)]" data-testid="demo-request-form-submit-button">
                      {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>) : "Request demo"}
                    </Button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </Container>
      </section>
    </div>
  );
}
